import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, message, Modal, Popconfirm, Table, Tag, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { createAppParam, deleteAppParam, listAppParams, updateAppParam } from "../services/appParamApi";

function payload(values, status) {
  return {
    parType: values.parType,
    parCode: values.parCode.trim().toUpperCase(),
    parName: values.parName.trim(),
    parValue: values.parValue?.trim() || null,
    description: values.description?.trim() || null,
    status,
  };
}

export default function GeneralSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const loadSequence = useRef(0);
  const [form] = Form.useForm();

  async function load(nextType = "") {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError("");
    try {
      const result = await listAppParams(nextType);
      if (sequence === loadSequence.current) setRows(result);
    } catch (failure) {
      if (sequence === loadSequence.current) setError(failure.message || "Không thể tải cấu hình chung");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate() {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ parType: "ACCOUNT_STATUS" });
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    form.setFieldsValue(row);
    setModalOpen(true);
  }

  function close() {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  }

  async function save(values) {
    if (saving) return;
    setSaving(true);
    try {
      const request = payload(values, editing ? editing.status : true);
      if (editing) await updateAppParam(editing.id, request);
      else await createAppParam(request);
      message.success(editing ? "Đã cập nhật cấu hình" : "Đã thêm cấu hình");
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      await load();
    } catch (failure) {
      message.error(failure.message || "Không thể lưu cấu hình");
    } finally {
      setSaving(false);
    }
  }

  async function remove(row) {
    if (saving || !row.status) return;
    setSaving(true);
    try {
      await deleteAppParam(row.id);
      message.success("Đã chuyển cấu hình sang ngừng sử dụng");
      await load();
    } catch (failure) {
      message.error(failure.message || "Không thể xóa cấu hình");
    } finally {
      setSaving(false);
    }
  }

  const columns = [
    { title: "STT", key: "serial", width: 70, align: "center", render: (_, __, index) => index + 1 },
    { title: "Loại tham số", dataIndex: "parType", width: 165 },
    { title: "Mã tham số", dataIndex: "parCode", width: 150 },
    { title: "Tên tham số", dataIndex: "parName", width: 190 },
    { title: "Giá trị tham số", dataIndex: "parValue", width: 150, render: (value) => value || "—" },
    { title: "Mô tả", dataIndex: "description", width: 250, render: (value) => value || "—" },
    { title: "Trạng thái", dataIndex: "status", width: 120, render: (value) => <Tag color={value ? "green" : "default"}>{value ? "Sử dụng" : "Ngừng dùng"}</Tag> },
    { title: "Thao tác", key: "actions", width: 125, render: (_, row) => <>
      <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(row)}>Sửa</Button>
      {row.status && <Popconfirm title="Cấu hình sẽ chuyển sang ngừng sử dụng. Xóa?" okText="Xóa" cancelText="Hủy" onConfirm={() => remove(row)}><Button type="link" danger icon={<DeleteOutlined />} disabled={saving}>Xóa</Button></Popconfirm>}
    </> },
  ];

  return (
    <div className="management-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Cấu hình chung</Typography.Title>
          <Typography.Text type="secondary">Quản lý tham số dùng chung trong APP_PARAM.</Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Thêm cấu hình</Button>
      </div>
      <div className="filter-bar">
        <Button icon={<ReloadOutlined />} onClick={() => load()}>Tải lại</Button>
      </div>
      {error && <Alert className="page-alert" type="error" showIcon message={error} action={<Button size="small" onClick={() => load()}>Thử lại</Button>} />}
      <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 1150 }} pagination={{ pageSize: 20 }} locale={{ emptyText: "Chưa có cấu hình" }} />
      <Modal title={editing ? "Sửa cấu hình" : "Thêm cấu hình"} open={modalOpen} onCancel={close} onOk={() => form.submit()} okText="Lưu" cancelText="Hủy" confirmLoading={saving} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          <Form.Item name="parType" label="Loại tham số" rules={[{ required: true, message: "Nhập loại tham số" }]}>
            <Input maxLength={50} disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item name="parCode" label="Mã tham số" extra="Mã được lưu trong dữ liệu tài khoản và không thể đổi sau khi tạo." rules={[{ required: true, message: "Nhập mã tham số" }]}>
            <Input maxLength={100} disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item name="parName" label="Tên tham số" rules={[{ required: true, message: "Nhập tên tham số" }]}><Input maxLength={255} /></Form.Item>
          <Form.Item name="parValue" label="Giá trị tham số"><Input maxLength={255} /></Form.Item>
          <Form.Item name="description" label="Mô tả"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
